// Cancellation feedback: one main reason, one follow up tied to it, and an
// optional comment. The codes are what the server stores and validates — they
// must match rhino-backend app/Services/Billing/CancellationFeedback.php.
// Labels are copy and can change freely.
//
// The reasons and follow ups are the Cancellation Feedback Playbook's reason
// tree, agreed with Jonathan on 2026-10-08 ("Other" is shown as "Something
// else"). commentPrompt is the playbook's optional prompt for that reason,
// shown as a hint under the optional comment box.

export const OTHER = "other";
export const MIN_OTHER_TEXT = 20;
export const MAX_OTHER_TEXT = 500;
export const MAX_COMMENT = 2000;

export const CANCELLATION_REASONS = [
  {
    code: "low_value",
    label: "I'm not getting enough value or results",
    question: "What best describes the problem?",
    followUps: [
      { code: "few_opportunities", label: "I didn't find enough good opportunities" },
      { code: "contacts_no_results", label: "The contact information didn't produce results" },
      { code: "intelligence_not_useful", label: "The storm or property intelligence wasn't useful enough" },
      { code: "cannot_measure_return", label: "I couldn't clearly measure my return" },
      { code: "not_used_enough", label: "I didn't use Rhino Recon enough to see results" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "data_quality",
    label: "The data wasn't accurate or useful enough",
    question: "Which data caused the most trouble?",
    commentPrompt: "If you remember a market, address, or example, please share it.",
    followUps: [
      { code: "homeowner_contacts", label: "Homeowner contact information" },
      { code: "property_information", label: "Property information" },
      { code: "storm_weather", label: "Storm or weather information" },
      { code: "territory_location", label: "Territory or location results" },
      { code: "not_enough_records", label: "There weren't enough available records" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "low_usage",
    label: "I'm not using Rhino Recon enough",
    question: "What prevented you from using it more?",
    followUps: [
      { code: "not_enough_time", label: "I didn't have enough time" },
      { code: "unsure_how_to_use", label: "I wasn't sure how to use it effectively" },
      { code: "team_did_not_adopt", label: "My team didn't adopt it" },
      { code: "workflow_fit", label: "It didn't fit our current workflow" },
      { code: "not_prospecting", label: "We weren't actively prospecting" },
      { code: "seasonal_business", label: "Our business is seasonal" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "price_budget",
    label: "It's too expensive for my business right now",
    question: "What best describes your situation?",
    followUps: [
      { code: "monthly_price_too_high", label: "The monthly price is too high" },
      { code: "usage_not_justified", label: "Our usage didn't justify the cost" },
      { code: "budget_changed", label: "Our business budget changed" },
      { code: "cheaper_option", label: "We found a less expensive option" },
      { code: "seasonal_need", label: "We only need Rhino Recon during certain seasons" },
      { code: "smaller_plan", label: "We need a smaller plan" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "missing_capability",
    label: "A feature or integration I need is missing",
    question: "What capability did you need most?",
    followUps: [
      { code: "crm_integration", label: "CRM integration" },
      { code: "export_or_api", label: "Exporting or API access" },
      { code: "team_management", label: "Better team or user management" },
      { code: "map_territory_filtering", label: "Better map, territory, or filtering tools" },
      { code: "reporting_tracking", label: "Better reporting or activity tracking" },
      { code: "data_coverage", label: "More data coverage" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "usability",
    label: "Rhino Recon was difficult or time-consuming to use",
    question: "Where did you have the most difficulty?",
    followUps: [
      { code: "getting_started", label: "Getting started or setting up" },
      { code: "understanding_platform", label: "Understanding how to use the platform" },
      { code: "search_filtering", label: "Searching or filtering" },
      { code: "map", label: "Working with the map" },
      { code: "exporting_results", label: "Exporting or managing results" },
      { code: "training_team", label: "Training my team" },
      { code: "too_many_steps", label: "The workflow required too many steps" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "technical",
    label: "I experienced technical problems",
    question: "What type of problem did you experience?",
    commentPrompt: "What happened?",
    followUps: [
      { code: "platform_slow", label: "The platform was slow" },
      { code: "features_broken", label: "Pages or features didn't work" },
      { code: "login_access", label: "Login or account-access problems" },
      { code: "export_download", label: "Export or download problems" },
      { code: "billing_subscription", label: "Billing or subscription problems" },
      { code: "integration_failed", label: "An integration didn't work" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "business_change",
    label: "My business needs have changed",
    question: "What changed?",
    followUps: [
      { code: "seasonal_slowdown", label: "We're experiencing a seasonal slowdown" },
      { code: "not_prospecting", label: "We're no longer actively prospecting" },
      { code: "smaller_sales_team", label: "We reduced the size of our sales team" },
      { code: "changed_markets", label: "We changed markets or service areas" },
      { code: "downsizing", label: "We're downsizing or restructuring" },
      { code: "business_closing", label: "The business is closing" },
      { code: "temporary_break", label: "We only need a temporary break" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "competitor",
    label: "I'm switching to another solution",
    question: "What was the main reason you chose another solution?",
    commentPrompt: "Which solution are you switching to?",
    followUps: [
      { code: "better_pricing", label: "Better pricing" },
      { code: "better_data", label: "Better or more accurate data" },
      { code: "more_features", label: "More features" },
      { code: "easier_to_use", label: "Easier to use" },
      { code: "better_integrations", label: "Better integrations" },
      { code: "better_support", label: "Better support or onboarding" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: "customer_success",
    label: "I had a poor support or onboarding experience",
    question: "What could we have done better?",
    commentPrompt: "Tell us what happened so we can address it.",
    followUps: [
      { code: "faster_response", label: "Faster response time" },
      { code: "better_onboarding", label: "Better initial onboarding" },
      { code: "more_training", label: "More training" },
      { code: "strategy_help", label: "Better help with strategy or implementation" },
      { code: "problem_unresolved", label: "A problem was never resolved" },
      { code: "team_experience", label: "The experience with our team was disappointing" },
      { code: OTHER, label: "Something else" },
    ],
  },
  {
    code: OTHER,
    label: "Something else",
    question: "What is the main reason you're leaving?",
    followUps: [],
  },
];

export const findReason = (code) =>
  CANCELLATION_REASONS.find((reason) => reason.code === code);

// Twenty non-whitespace characters, at least one a letter or digit, so
// one-word answers, "....." or "  NA  " do not count as an explanation.
// Mirrors the server check.
export const isMeaningfulText = (text) => {
  const visible = String(text || "").replace(/\s+/gu, "");
  return (
    Array.from(visible).length >= MIN_OTHER_TEXT && /[\p{L}\p{N}]/u.test(visible)
  );
};
